import { useEffect, useMemo, useState } from "react"

import { toast } from "sonner"

import { useUpdateCollectionsConfig } from "@/hooks/useCollections"
import {
  CONFIG_DEFAULTS,
  type ConfigFormValues,
  configFormIsDirty,
  formFromConfig,
} from "@/lib/configForm"
import { CollectionsConfig } from "@/types/collections"

/** Shared with the page: cost examples and the vehicle type dialog price against unsaved values. */
export function useGlobalConfigForm(config: CollectionsConfig | undefined) {
  const [configForm, setConfigForm] = useState<ConfigFormValues>(CONFIG_DEFAULTS)

  useEffect(() => {
    if (config) {
      setConfigForm(formFromConfig(config))
    }
  }, [config])

  const updateConfig = useUpdateCollectionsConfig()

  const hasUnsavedChanges = useMemo(
    () => (config ? configFormIsDirty(configForm, config) : false),
    [config, configForm],
  )

  const handleSaveConfig = async () => {
    if (
      configForm.driver_wage_rate_per_hour < 0 ||
      configForm.fleet_cost_per_month_gbp < 0 ||
      configForm.on_site_charging_cost_per_kwh < 0 ||
      configForm.default_cost_per_kwh < 0 ||
      configForm.oaklands_cost_per_kwh < 0 ||
      configForm.internal_charging_range_pct < 0 ||
      configForm.internal_charging_range_pct > 100 ||
      configForm.planned_mileage_percentage_add < 0 ||
      configForm.haulage_min_empty_pct < 0 ||
      configForm.haulage_min_empty_pct > 100 ||
      configForm.haulage_min_extended_minutes < 0
    ) {
      toast.error("Validation Error", {
        description: "Values cannot be negative",
      })
      return
    }

    if (
      configForm.uk_pallet_ratio_sort_bag <= 0 ||
      configForm.uk_pallet_ratio_cage <= 0 ||
      configForm.uk_pallet_ratio_magnum <= 0 ||
      configForm.uk_pallet_ratio_eur_pallet <= 0 ||
      configForm.uk_pallet_ratio_uk_pallet <= 0
    ) {
      toast.error("Validation Error", {
        description: "UK pallet ratios must be greater than 0",
      })
      return
    }

    try {
      await updateConfig.mutateAsync({
        driver_wage_rate_per_hour: configForm.driver_wage_rate_per_hour,
        fleet_cost_per_month_gbp: configForm.fleet_cost_per_month_gbp,
        driver_max_hours_per_day: configForm.driver_max_hours_per_day,
        haulage_min_empty_pct: configForm.haulage_min_empty_pct,
        haulage_min_extended_minutes: configForm.haulage_min_extended_minutes,
        on_site_charging_cost_per_kwh: configForm.on_site_charging_cost_per_kwh,
        default_cost_per_kwh: configForm.default_cost_per_kwh,
        oaklands_cost_per_kwh: configForm.oaklands_cost_per_kwh,
        internal_charging_range_pct: configForm.internal_charging_range_pct,
        planned_mileage_percentage_add: configForm.planned_mileage_percentage_add,
        uk_pallet_ratio_sort_bag: configForm.uk_pallet_ratio_sort_bag,
        uk_pallet_ratio_cage: configForm.uk_pallet_ratio_cage,
        uk_pallet_ratio_magnum: configForm.uk_pallet_ratio_magnum,
        uk_pallet_ratio_eur_pallet: configForm.uk_pallet_ratio_eur_pallet,
        uk_pallet_ratio_uk_pallet: configForm.uk_pallet_ratio_uk_pallet,
      })
      toast.success("Success", {
        description: "Configuration saved successfully",
      })
    } catch (error) {
      toast.error("Error", {
        description: error instanceof Error ? error.message : "Failed to save configuration",
      })
    }
  }

  return {
    configForm,
    setConfigForm,
    hasUnsavedChanges,
    handleSaveConfig,
    isSaving: updateConfig.isPending,
  }
}
